import type { PhotoSize, SeriesQuery, Video } from 'api';
import { Playlist, useSeriesLazyQuery } from 'api';
import ContentContainer from 'components/content-container';
import Grid from 'components/grid';
import Col from 'components/grid/col';
import Pagination from 'components/pagination';
import { Tabs } from 'components/tabs';
import TalkCard from 'components/talk-card';
import TalkCardSkeleton from 'components/talk-card/talk-card-skeleton';
import { getBadgeText } from 'components/talk-card/utils';
import { client } from 'f';
import { useSetSourceContext } from 'lib/business/source-context';
import getImageFromSet from 'lib/get-image-from-set';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';

const VIDEOS_PER_PAGE = 12;

type SeriesVideosProps = {
  seasons: Playlist[];
  seriesSlug: string;
};

const formatSeasons = (data: SeriesQuery): Playlist[] => {
  const seasons: Playlist[] = [];

  if (data?.series?.playlists?.nodes) {
    const playlists = data.series.playlists.nodes.filter(
      (item): item is NonNullable<typeof item> => item != null
    );
    playlists.forEach(season => {
      seasons.unshift(season as Playlist); // recent seasons first
    });
  }

  return seasons;
};

type TalkCardGridProps = {
  slug: string;
  videos: Video[];
  loading: boolean;
};

function TalkCardGrid({ slug, videos, loading }: TalkCardGridProps) {
  const setContentDiscoveryContext = useSetSourceContext(
    'content_discovery_context'
  );

  return (
    <div className="pt-8 xl:pt-10">
      <Grid>
        {loading
          ? [...new Array(VIDEOS_PER_PAGE)].map((_, index) => (
              <Col sm={2} md={6} lg={4} xl={3} key={index} className="mb-8">
                <TalkCardSkeleton size="m" />
              </Col>
            ))
          : videos.map(video => {
              const imageUrl =
                getImageFromSet(video.primaryImageSet as PhotoSize[], '16x9') ||
                '';
              const badgeText = getBadgeText(video.type.name) || undefined;

              return (
                <Col
                  key={video.slug}
                  sm={2}
                  md={6}
                  lg={4}
                  xl={3}
                  className="mb-8"
                >
                  <TalkCard
                    key={video.slug}
                    imageUrl={imageUrl}
                    href={`/talks/${video.slug}?referrer=playlist-${slug}`}
                    title={video.title}
                    duration={video.duration}
                    size="m"
                    numberOfLinesToClamp={5}
                    description={video.description || ''}
                    presenterDisplayName={video.presenterDisplayName || ''}
                    badgeText={badgeText}
                    onClick={() => {
                      setContentDiscoveryContext('series');
                    }}
                  />
                </Col>
              );
            })}
      </Grid>
    </div>
  );
}

export default function SeriesVideos({
  seriesSlug,
  seasons: initialSeasons
}: SeriesVideosProps): React.ReactNode {
  const [seasons, setSeasons] = useState<Playlist[]>(initialSeasons);
  const containerRef = useRef<HTMLDivElement>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const hasMultipleSeasons = seasons.length > 1;
  const [selectedTabIndex, setSelectedTabIndex] = useState(() => {
    if (!client) {
      return 0;
    }

    const seasonNumber = window.location.hash.match(/#season_(\d+)/i)?.[1];
    if (seasonNumber == null) {
      return 0;
    }

    const seasonIndex = seasons.findIndex(
      season => season.seasonNumber === seasonNumber
    );

    return seasonIndex === -1 ? 0 : seasonIndex;
  });

  const [getNewPageData, { data, loading }] = useSeriesLazyQuery({
    variables: {
      slug: seriesSlug,
      // why would a cursor be set as string, i have no idea, yet here we are
      after: String((currentPage - 1) * VIDEOS_PER_PAGE),
      before: String(currentPage * VIDEOS_PER_PAGE + 1)
    },
    ssr: false
  });

  const handleChangePage = (newPage: number) => {
    setCurrentPage(newPage);
    getNewPageData();
    if (containerRef.current) {
      requestAnimationFrame(() => {
        containerRef.current?.scrollIntoView({
          behavior: 'smooth'
        });
      });
    }
  };

  useEffect(() => {
    if (data) {
      setSeasons(formatSeasons(data));
    }
  }, [data]);

  const router = useRouter();
  const onTabChange = (index: number) => {
    setSelectedTabIndex(index);
    const { seasonNumber } = seasons[index];

    router.replace(`#season_${seasonNumber}`, undefined, {
      scroll: false,
      shallow: true
    });
  };

  const tabItems = seasons.map(season => {
    const defaultMessage =
      seriesSlug === 'the_audacious_project'
        ? season.seasonLabel
        : `Season ${season.seasonLabel || season.seasonNumber}`;

    const filteredVideos = (season.videos?.nodes || []).filter(
      (i): i is NonNullable<typeof i> => i !== null
    );

    return {
      title: defaultMessage || '',
      content: (
        <>
          <TalkCardGrid
            loading={loading}
            videos={filteredVideos}
            slug={season.slug}
          />
          {season.videos?.totalCount > VIDEOS_PER_PAGE && (
            <Pagination
              totalPageCount={Math.ceil(
                season.videos.totalCount / VIDEOS_PER_PAGE
              )}
              activeIndex={currentPage}
              onChange={(index, e) => {
                e?.preventDefault();
                handleChangePage(index);
              }}
            />
          )}
        </>
      )
    };
  });

  return (
    <ContentContainer className="bg-white md:pb-2 xl:pb-12">
      <div ref={containerRef}>
        {hasMultipleSeasons ? (
          <Tabs
            items={tabItems}
            selectedIndex={selectedTabIndex}
            onChange={onTabChange}
            tabClassName="md:pl-4 md:pr-4 xl:pl-5 xl:pr-5"
          />
        ) : (
          <div>
            <TalkCardGrid
              loading={loading}
              videos={(seasons[0]?.videos?.nodes || []).filter(
                (i): i is NonNullable<typeof i> => i !== null
              )}
              key={seasons[0]?.id}
              slug={seasons[0]?.slug}
            />
            {seasons[0]?.videos?.totalCount > VIDEOS_PER_PAGE && (
              <div className="mb-16 mt-8">
                <Pagination
                  totalPageCount={Math.ceil(
                    seasons[0].videos.totalCount / VIDEOS_PER_PAGE
                  )}
                  activeIndex={currentPage}
                  onChange={(index, e) => {
                    e?.preventDefault();
                    handleChangePage(index);
                  }}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </ContentContainer>
  );
}
